import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Hook that applies user theme preferences to the document
 */
export const useUserTheme = () => {
  const { user } = useAuth();
  const appliedThemeRef = useRef<string | null>(null);

  // Set up a MutationObserver to watch for class changes
  useEffect(() => {
    if (!user?.preferences?.theme?.mode) return;

    const targetMode = user.preferences.theme.mode;

    // Create an observer to watch for class removal
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const htmlElement = document.documentElement;
          const shouldHaveDarkClass = targetMode === 'dark';
          const hasDarkClass = htmlElement.classList.contains('theme-dark');

          if (shouldHaveDarkClass && !hasDarkClass) {
            console.log('[useUserTheme] Dark class was removed, reapplying...');
            htmlElement.classList.add('theme-dark');
            htmlElement.setAttribute('data-theme', 'dark');

            // Also reapply dark mode CSS variables - must match theme.css dark mode values
            htmlElement.style.setProperty('--background-primary', '#1a1a1a');
            htmlElement.style.setProperty('--background-secondary', '#2d2d2d');
            htmlElement.style.setProperty('--background-tertiary', '#383838');
            htmlElement.style.setProperty('--text-primary', '#f5f5f5');
            htmlElement.style.setProperty('--text-secondary', '#b8b8b8');
            htmlElement.style.setProperty('--text-tertiary', '#8a8a8a');
            htmlElement.style.setProperty('--border-light', '#404040');
          }
        }
      });
    });

    // Start observing
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, [user?.preferences?.theme?.mode]);

  useEffect(() => {
    if (!user?.preferences?.theme) {
      console.log('[useUserTheme] No theme preferences found');
      // Don't clear theme if it was previously set
      const currentTheme = document.documentElement.getAttribute('data-theme');
      if (currentTheme) {
        console.log('[useUserTheme] Keeping existing theme:', currentTheme);
      }
      return;
    }

    const themePrefs = user.preferences.theme;
    const { mode, primaryColor, fontSize, customPalette } = typeof themePrefs === 'object' ? themePrefs : { mode: themePrefs, primaryColor: undefined, fontSize: undefined, customPalette: undefined };
    console.log('[useUserTheme] Applying theme:', { mode, primaryColor, fontSize, hasCustomPalette: !!customPalette });
    console.log('[useUserTheme] Mode type:', typeof mode, 'Mode value:', mode, 'Is dark?:', mode === 'dark');

    // Apply theme mode
    if (mode === 'dark') {
      console.log('[useUserTheme] Applying dark theme styles...');
      appliedThemeRef.current = 'dark';
      document.documentElement.classList.add('theme-dark');
      document.documentElement.setAttribute('data-theme', 'dark');

      // Apply dark mode colors - must match theme.css dark mode values
      document.documentElement.style.setProperty('--background-primary', '#1a1a1a');
      document.documentElement.style.setProperty('--background-secondary', '#2d2d2d');
      document.documentElement.style.setProperty('--background-tertiary', '#383838');
      document.documentElement.style.setProperty('--text-primary', '#f5f5f5');
      document.documentElement.style.setProperty('--text-secondary', '#b8b8b8');
      document.documentElement.style.setProperty('--text-tertiary', '#8a8a8a');
      document.documentElement.style.setProperty('--border-light', '#404040');
    } else {
      console.log('[useUserTheme] Applying light theme styles (mode was not dark)...');
      appliedThemeRef.current = 'light';
      document.documentElement.classList.remove('theme-dark');
      document.documentElement.setAttribute('data-theme', 'light');

      // Apply light mode colors - must match theme.css values
      document.documentElement.style.setProperty('--background-primary', '#FFFFFF');
      document.documentElement.style.setProperty('--background-secondary', '#F9FAFB');
      document.documentElement.style.setProperty('--background-tertiary', '#E5E7EB');
      document.documentElement.style.setProperty('--text-primary', '#212121');
      document.documentElement.style.setProperty('--text-secondary', '#616161');
      document.documentElement.style.setProperty('--text-tertiary', '#9E9E9E');
      document.documentElement.style.setProperty('--border-light', '#EEEEEE');
    }

    // Apply custom palette (if present, overrides primaryColor)
    if (customPalette) {
      console.log('[useUserTheme] Applying custom palette', customPalette);

      // Apply primary shades
      if (customPalette.primary?.shades) {
        Object.entries(customPalette.primary.shades).forEach(([shade, color]) => {
          document.documentElement.style.setProperty(`--primary-${shade}`, color);
        });
      } else if (typeof customPalette.primary === 'string') {
        // Handle simplified format where primary is just a hex string
        document.documentElement.style.setProperty('--primary-500', customPalette.primary);
      }

      // Apply secondary shades
      if (customPalette.secondary?.shades) {
        Object.entries(customPalette.secondary.shades).forEach(([shade, color]) => {
          document.documentElement.style.setProperty(`--secondary-${shade}`, color);
        });
      } else if (typeof customPalette.secondary === 'string') {
        // Handle simplified format where secondary is just a hex string
        document.documentElement.style.setProperty('--secondary-500', customPalette.secondary);
      }

      // Apply neutral shades
      if (customPalette.neutral?.shades) {
        Object.entries(customPalette.neutral.shades).forEach(([shade, color]) => {
          document.documentElement.style.setProperty(`--neutral-${shade}`, color);
        });
      } else if (typeof customPalette.neutral === 'string') {
        // Handle simplified format where neutral is just a hex string
        document.documentElement.style.setProperty('--neutral-500', customPalette.neutral);
      }

      // Apply semantic colors with light/dark variants
      if (customPalette.success) {
        document.documentElement.style.setProperty('--success-default', customPalette.success);
        document.documentElement.style.setProperty('--success-light', customPalette.success + '20');
        document.documentElement.style.setProperty('--success-dark', darkenColor(customPalette.success, 15));
        document.documentElement.style.setProperty('--success-500', customPalette.success);
      }

      if (customPalette.warning) {
        document.documentElement.style.setProperty('--warning-default', customPalette.warning);
        document.documentElement.style.setProperty('--warning-light', customPalette.warning + '20');
        document.documentElement.style.setProperty('--warning-dark', darkenColor(customPalette.warning, 15));
        document.documentElement.style.setProperty('--warning-500', customPalette.warning);
      }

      if (customPalette.error) {
        document.documentElement.style.setProperty('--error-default', customPalette.error);
        document.documentElement.style.setProperty('--error-light', customPalette.error + '20');
        document.documentElement.style.setProperty('--error-dark', darkenColor(customPalette.error, 15));
        document.documentElement.style.setProperty('--error-500', customPalette.error);
      }

      if (customPalette.info) {
        document.documentElement.style.setProperty('--info-default', customPalette.info);
        document.documentElement.style.setProperty('--info-light', customPalette.info + '20');
        document.documentElement.style.setProperty('--info-dark', darkenColor(customPalette.info, 15));
        document.documentElement.style.setProperty('--info-500', customPalette.info);
      }

      // Apply background colors
      if (customPalette.backgroundPrimary) {
        document.documentElement.style.setProperty('--background-primary', customPalette.backgroundPrimary);
      }
      if (customPalette.backgroundSecondary) {
        document.documentElement.style.setProperty('--background-secondary', customPalette.backgroundSecondary);
      }
      if (customPalette.backgroundTertiary) {
        document.documentElement.style.setProperty('--background-tertiary', customPalette.backgroundTertiary);
      }

      // Apply text colors
      if (customPalette.textPrimary) {
        document.documentElement.style.setProperty('--text-primary', customPalette.textPrimary);
      }
      if (customPalette.textSecondary) {
        document.documentElement.style.setProperty('--text-secondary', customPalette.textSecondary);
      }
      if (customPalette.textTertiary) {
        document.documentElement.style.setProperty('--text-tertiary', customPalette.textTertiary);
      }

      // Apply border colors
      if (customPalette.borderLight) {
        document.documentElement.style.setProperty('--border-light', customPalette.borderLight);
      }
      if (customPalette.borderMedium) {
        document.documentElement.style.setProperty('--border-medium', customPalette.borderMedium);
      }
      if (customPalette.borderDark) {
        document.documentElement.style.setProperty('--border-dark', customPalette.borderDark);
      }

      // Update legacy primary-based colors for backward compatibility
      const primaryBase = customPalette.primary?.baseColor || customPalette.primary?.shades?.['500'];
      if (primaryBase) {
        document.documentElement.style.setProperty('--primary-default', primaryBase);
        document.documentElement.style.setProperty('--primary-hover', darkenColor(primaryBase, 10));
        document.documentElement.style.setProperty('--primary-active', darkenColor(primaryBase, 20));
      }

      console.log('[useUserTheme] Custom palette applied successfully');
    } else if (primaryColor) {
      // Apply primary color (legacy single-color customization)
      console.log('[useUserTheme] Primary color value:', primaryColor, 'Type:', typeof primaryColor);
      console.log('[useUserTheme] Applying primary color:', primaryColor);
      // Convert hex to RGB for variations
      const hex = primaryColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Generate color variations
      document.documentElement.style.setProperty('--primary-500', primaryColor);
      console.log('[useUserTheme] Set --primary-500 to:', primaryColor);

      // Re-apply after a short delay to ensure it overrides any CSS loading
      setTimeout(() => {
        document.documentElement.style.setProperty('--primary-500', primaryColor);
        console.log('[useUserTheme] Re-applied --primary-500:', primaryColor);
      }, 100);
      document.documentElement.style.setProperty('--primary-600', darkenColor(primaryColor, 10));
      document.documentElement.style.setProperty('--primary-400', lightenColor(primaryColor, 10));
      document.documentElement.style.setProperty('--primary-300', lightenColor(primaryColor, 20));
      document.documentElement.style.setProperty('--primary-700', darkenColor(primaryColor, 20));

      // Update other primary-based colors
      document.documentElement.style.setProperty('--primary-default', primaryColor);
      document.documentElement.style.setProperty('--primary-hover', darkenColor(primaryColor, 10));
      document.documentElement.style.setProperty('--primary-active', darkenColor(primaryColor, 20));
    }

    // Apply font size
    if (fontSize) {
      const fontSizes = {
        small: {
          base: '14px',
          sm: '12px',
          md: '14px',
          lg: '16px',
          xl: '18px',
          '2xl': '20px',
          '3xl': '24px'
        },
        medium: {
          base: '16px',
          sm: '14px',
          md: '16px',
          lg: '18px',
          xl: '20px',
          '2xl': '24px',
          '3xl': '30px'
        },
        large: {
          base: '18px',
          sm: '16px',
          md: '18px',
          lg: '20px',
          xl: '24px',
          '2xl': '30px',
          '3xl': '36px'
        }
      };

      const sizes = fontSizes[fontSize as keyof typeof fontSizes] || fontSizes.medium;

      Object.entries(sizes).forEach(([key, value]) => {
        document.documentElement.style.setProperty(`--font-size-${key}`, value);
      });
    }

    // Save to localStorage for persistence
    localStorage.setItem('user_preferences', JSON.stringify(user.preferences));

    console.log('[useUserTheme] Theme applied successfully');
  }, [user?.preferences?.theme]);

  return null;
};

// Helper functions for color manipulation
function lightenColor(color: string, percent: number): string {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const newR = Math.min(255, r + (255 - r) * (percent / 100));
  const newG = Math.min(255, g + (255 - g) * (percent / 100));
  const newB = Math.min(255, b + (255 - b) * (percent / 100));

  return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`;
}

function darkenColor(color: string, percent: number): string {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const newR = Math.max(0, r - r * (percent / 100));
  const newG = Math.max(0, g - g * (percent / 100));
  const newB = Math.max(0, b - b * (percent / 100));

  return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`;
}

export default useUserTheme;